import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { LoggingService } from '@app/service/logging-service/logging.service';
import { versions } from '../../environments/versions';

/**
 * Attach the payment request ID to the X-PRID header of every outgoing
 * request. Done in favor of using a session cookie or session attribute
 * as Google chrome abuses the http session standards by persisting
 * session data after the user closes the browser.
 */
@Injectable()
export class CheckoutInterceptor implements HttpInterceptor {
  constructor(
    private route: ActivatedRoute,
    private loggingService: LoggingService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const id: string = this.route.snapshot.queryParams.id;

    // Set the id header to default to an empty string as the Angular HttpClient
    // will throw an error if a header value is undefined
    let headers = new HttpHeaders();
    headers = headers.append('X-PRID', id ? id : '');
    headers = headers.append(
      'X-FRONTEND-CONTEXT-ID',
      this.loggingService.getContextID()
    );
    headers = headers.append(
      'X-FRONTEND-PERSISTENT-ID',
      this.loggingService.getPersistentId()
    );
    headers = headers.append(
      'x-client-version',
      versions.branch + ':' + versions.revision
    );
    headers = headers.append('X-LANGUAGE', navigator.language);
    headers = headers.append('X-SCREEN-WIDTH', screen.width.toString());
    headers = headers.append('X-SCREEN-HEIGHT', screen.height.toString());
    headers = headers.append('X-COLOR-DEPTH', screen.colorDepth.toString());
    headers = headers.append('X-TIMEZONE', this.getUTCOffset());
    headers = headers.append('X-MAJOR-OS-VERSION', this.getMajorOSVersion());
    headers = headers.append('X-MINOR-OS-VERSION', this.getMinorOSVersion());

    const cloneReq = req.clone({
      headers: headers,
    });

    return next.handle(cloneReq);
  }

  private getUTCOffset(): string {
    const janOffset = new Date(2026, 0, 2).getTimezoneOffset();
    const junOffset = new Date(2026, 5, 2).getTimezoneOffset();

    return String(Math.max(janOffset, junOffset));
  }

  private getMajorOSVersion(): string {
    const userAgent = navigator.userAgent;

    // Windows
    if (userAgent.includes('Windows NT')) {
      const match = userAgent.match(/Windows NT (\d+)/);
      return match ? match[1] : 'Unknown';
    }

    // iOS
    if (
      userAgent.includes('iPhone OS') ||
      userAgent.includes('CPU iPhone OS') ||
      userAgent.includes('CPU OS')
    ) {
      const match = userAgent.match(/(?:iPhone OS|CPU (?:iPhone )?OS) (\d+)/);
      return match ? match[1] : 'Unknown';
    }

    // macOS
    if (userAgent.includes('Mac OS X')) {
      const match = userAgent.match(/Mac OS X (\d+)/);
      return match ? match[1] : 'Unknown';
    }

    // Android
    if (userAgent.includes('Android')) {
      const match = userAgent.match(/Android (\d+)/);
      return match ? match[1] : 'Unknown';
    }

    return 'Unknown';
  }

  private getMinorOSVersion(): string {
    const userAgent = navigator.userAgent;

    // Windows
    if (userAgent.includes('Windows NT')) {
      const match = userAgent.match(/Windows NT \d+\.(\d+)/);
      return match ? match[1] : '0';
    }

    // iOS
    if (
      userAgent.includes('iPhone OS') ||
      userAgent.includes('CPU iPhone OS') ||
      userAgent.includes('CPU OS')
    ) {
      const match = userAgent.match(
        /(?:iPhone OS|CPU (?:iPhone )?OS) \d+_(\d+)/
      );
      return match ? match[1] : '0';
    }

    // macOS
    if (userAgent.includes('Mac OS X')) {
      const match = userAgent.match(/Mac OS X \d+_(\d+)/);
      return match ? match[1] : '0';
    }

    // Android
    if (userAgent.includes('Android')) {
      const match = userAgent.match(/Android \d+\.(\d+)/);
      return match ? match[1] : '0';
    }

    return '0';
  }
}
